import React, { useEffect } from 'react';
import { globalEllipsisTextStyles } from 'lib';
import {
    Box,
    InputAdornment,
    ListItem,
    MenuItem,
    Typography,
} from '@mui/material';
import {
    Select,
    TextField as MuiTextField,
    Autocomplete,
    SearchIcon,
} from '@biq/apex-ui';
import { AutocompleteChainSelector } from 'components';
import { Controller } from 'react-hook-form';
import { pipelineFormStyles as formStyles } from 'pages/pipelines/CreateOrUpdatePipeline/components/pipeline-form-helper';
import {
    CUSTOM,
    FieldNameTypeFormProps,
    fieldNameTypeFormStyles,
    fieldNameTypeOptions,
    FROM_DICTIONARY,
    NONE,
} from './field-name-type-helper';
import { useDictionaryList } from 'hooks';

export const FieldNameType = ({
    control,
    errors,
    watch,
    setValue,
    clearErrors,
}: FieldNameTypeFormProps): React.JSX.Element => {
    const fieldNameType = watch('fieldNameType');
    const dictionary = watch('dictionary');

    const { data: dictionaryData } = useDictionaryList({
        enabled: fieldNameType === FROM_DICTIONARY,
    });

    useEffect(() => {
        if (fieldNameType === NONE) {
            setValue('customFieldName', '');
            setValue('dictionary', '');
            setValue('fieldName', '');
            clearErrors(['customFieldName', 'dictionary', 'fieldName']);
        } else if (fieldNameType === CUSTOM) {
            setValue('dictionary', '');
            setValue('fieldName', '');
            clearErrors(['dictionary', 'fieldName']);
        } else if (fieldNameType === FROM_DICTIONARY) {
            setValue('customFieldName', '');
            clearErrors(['customFieldName']);
        }
    }, [fieldNameType, setValue, clearErrors]);

    const getAttributeList = (): string[] => {
        const selectedDictionary = dictionaryData?.data.find(
            (item) => item.name === dictionary
        );
        return selectedDictionary?.attributes
            ? selectedDictionary.attributes.map((attr) => attr.name)
            : [];
    };

    return (
        <>
            <Box
                sx={{
                    ...formStyles.fieldGroup,
                    ...fieldNameTypeFormStyles.formFieldHeight,
                }}
            >
                <Controller
                    name='fieldNameType'
                    control={control}
                    render={({ field }): React.JSX.Element => (
                        <Select
                            {...field}
                            label={'Field Name Type'}
                            required={true}
                            variant='outlined'
                            onChange={(e): void => {
                                field.onChange(e);
                            }}
                            error={!!errors.fieldNameType}
                            errorText={
                                errors.fieldNameType?.message as
                                    | string
                                    | undefined
                            }
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        ...fieldNameTypeFormStyles.fieldNameTypeOptionsList,
                                    },
                                },
                            }}
                        >
                            {fieldNameTypeOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </Box>

            {fieldNameType === CUSTOM && (
                <Box
                    sx={fieldNameTypeFormStyles.customFieldNameContainerStyles}
                >
                    <Box
                        sx={{
                            ...fieldNameTypeFormStyles.customFieldNameStyles,
                            ...fieldNameTypeFormStyles.formFieldHeight,
                        }}
                    >
                        <Controller
                            name='customFieldName'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <MuiTextField
                                    {...field}
                                    variant='outlined'
                                    label='Custom Field Name'
                                    value={field.value}
                                    onChange={(event): void => {
                                        field.onChange(event);
                                    }}
                                    onBlur={(value): void => {
                                        field.onChange(
                                            value.target.value.trim()
                                        );
                                    }}
                                    required
                                    error={!!errors?.customFieldName}
                                    errorText={
                                        errors?.customFieldName?.message as
                                            | string
                                            | undefined
                                    }
                                />
                            )}
                        />
                    </Box>
                </Box>
            )}

            {fieldNameType === FROM_DICTIONARY && (
                <Box
                    sx={fieldNameTypeFormStyles.customFieldNameContainerStyles}
                >
                    <Box sx={fieldNameTypeFormStyles.customFieldNameStyles}>
                        <Box
                            sx={{
                                ...fieldNameTypeFormStyles.formFieldHeight,
                            }}
                        >
                            <AutocompleteChainSelector
                                tooltipTitle={dictionary ?? ''}
                                controlName='dictionary'
                                selectedChain={dictionary}
                                control={control}
                                label='Dictionary'
                                error={!!errors.dictionary}
                                errorText={
                                    (errors.dictionary?.message as
                                        | string
                                        | undefined) ?? ''
                                }
                                autocompleteStyles={
                                    fieldNameTypeFormStyles.dictionarySelect
                                }
                                hasSearchIcon={false}
                                handleOnChange={(): void => {
                                    setValue('fieldName', '' as any);
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                ...formStyles.fieldGroup,
                                ...fieldNameTypeFormStyles.formFieldHeight,
                            }}
                        >
                            <Controller
                                name='fieldName'
                                control={control}
                                render={({ field }): React.JSX.Element => (
                                    <Autocomplete
                                        {...field}
                                        value={field.value}
                                        disableClearable={!field.value}
                                        sx={
                                            fieldNameTypeFormStyles.dictionarySelect
                                        }
                                        options={getAttributeList()}
                                        onChange={(_, value): void =>
                                            field.onChange(value ?? '')
                                        }
                                        getOptionLabel={(option): string =>
                                            option ?? ''
                                        }
                                        ListboxProps={{
                                            sx: {
                                                ...fieldNameTypeFormStyles.stepOptionListConatiner,
                                            },
                                        }}
                                        renderOption={(
                                            props,
                                            option
                                        ): React.JSX.Element => (
                                            <ListItem {...props}>
                                                <Typography
                                                    sx={
                                                        globalEllipsisTextStyles
                                                    }
                                                >
                                                    {option}
                                                </Typography>
                                            </ListItem>
                                        )}
                                        renderInput={(
                                            params
                                        ): React.JSX.Element => (
                                            <MuiTextField
                                                {...params}
                                                size='medium'
                                                variant='outlined'
                                                label={'Field Name'}
                                                required
                                                error={!!errors.fieldName}
                                                errorText={
                                                    errors.fieldName
                                                        ?.message as
                                                        | string
                                                        | undefined
                                                }
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: [
                                                        <InputAdornment
                                                            key='searchIcon'
                                                            position='start'
                                                        >
                                                            <SearchIcon />
                                                        </InputAdornment>,
                                                    ],
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                </Box>
            )}
        </>
    );
};
